import type {ComponentType} from 'react';

export enum AccountConfigModeType {
    NETAPP_MODEL_DEFAULT = 'NETAPP_MODEL_DEFAULT',
    CUSTOMER_MODEL = 'USER_MODEL',
    DISABLED = 'DISABLED',
}

export type DisabledMode = {
    type: AccountConfigModeType.DISABLED;
};

export type NetAppModelDefaultMode = {
    type: AccountConfigModeType.NETAPP_MODEL_DEFAULT;
};

/**
 * Customer model config used as an **input** (save / validate requests).
 * Both `inferenceProfileId` and `credentialsId` are required here because
 * the backend rejects the request without them.
 */
export type CustomerModelConfig = {
    provider: 'BEDROCK';
    inferenceProfileId: string;
    credentialsId: string;
    region?: string;
};

/**
 * Customer model config as it arrives in a **response** from the backend.
 * The backend's `aiConfigurationModeResponseSchema` marks all model sub-fields
 * as optional, so the UI must not assume they are present.
 */
export type CustomerModelConfigResponse = {
    provider: 'BEDROCK';
    inferenceProfileId?: string;
    credentialsId?: string;
    region?: string;
};

export type CustomerModelMode = {
    type: AccountConfigModeType.CUSTOMER_MODEL;
    model: CustomerModelConfig;
};

/**
 * Variant of `CustomerModelMode` that mirrors what the backend actually sends:
 * the `model` object itself may be absent and its fields are all optional.
 */
export type CustomerModelModeResponse = {
    type: AccountConfigModeType.CUSTOMER_MODEL;
    model?: CustomerModelConfigResponse;
};

export type AccountConfigMode = DisabledMode | NetAppModelDefaultMode | CustomerModelMode;

/** Discriminated union for the backend response – model fields are all optional. */
export type AccountConfigModeResponse =
    | DisabledMode
    | NetAppModelDefaultMode
    | CustomerModelModeResponse;

export type AccountConfigResponse = {
    mode: AccountConfigModeResponse;
    updateTime?: string;
};

/**
 * Normalizes a raw `AccountConfigModeResponse` received from the API into the
 * stricter `AccountConfigMode` used internally by the UI.
 *
 * When the backend returns a `CUSTOMER_MODEL` mode without providing (or only
 * partially providing) the model sub-fields, this function fills in empty-string
 * defaults so downstream code can always read `mode.model.inferenceProfileId`
 * and `mode.model.credentialsId` without guarding for `undefined`.
 */
export const normalizeAccountConfigModeResponse = (
    mode: AccountConfigModeResponse,
): AccountConfigMode => {
    if (mode.type !== AccountConfigModeType.CUSTOMER_MODEL) {
        return mode;
    }
    return {
        ...mode,
        model: {
            provider: 'BEDROCK',
            inferenceProfileId: mode.model?.inferenceProfileId ?? '',
            credentialsId: mode.model?.credentialsId ?? '',
            region: mode.model?.region,
        },
    };
};

export type SaveConfigRequest = {
    mode:
        | { type: AccountConfigModeType.DISABLED }
        | { type: AccountConfigModeType.NETAPP_MODEL_DEFAULT }
        | { type: AccountConfigModeType.CUSTOMER_MODEL; model: CustomerModelConfig };
};

export type ValidateConfigRequest = {
    mode: {
        type: AccountConfigModeType.CUSTOMER_MODEL;
        model: CustomerModelConfig;
    };
};

export type ValidateConfigResponse = {
    valid: boolean;
};

export type StatusSummaryCardProps = {
    accessModeType: AccountConfigMode['type'];
    StatusIcon: ComponentType<any>;
};

export type NetAppManagedBedrockSelectionProps = {
    isChecked: boolean;
    onChange: () => void;
};

export type CustomerModelForm = {
    handleFormSubmit: (callback: () => void) => () => void;
    formState: {
        region?: {value?: string} | null;
        awsCredentials?: unknown;
        inferenceProfileArn?: string;
    };
};

export type HandleCustomerModelFormChange = (_valueOrEvent: any, _Event: any) => void

export type CustomerManagedBedrockSelectionProps = {
    isChecked: boolean;
    onChange: () => void;
    customerModelForm: CustomerModelForm;
    handleCustomerModelFormChange: HandleCustomerModelFormChange;
    regionModelValue?: string;
    credentialsModelValue?: string;
};

export type AiSettingsOffSelectionProps = {
    isChecked: boolean;
    onChange: () => void;
};
